import { useDropzone } from "react-dropzone";
import classNames from "classnames";
import { useTranslation } from "react-i18next";
import FontAwesomeIcon from "components/FontAwesomeIcon/FontAwesomeIcon";
import { Spinner } from "components/Loaders";
import LoadingTimelyMessager from "components/Loaders/LoadingTimelyMessager";
import useDocumentPreview from "hooks/useDocumentPreview";

const BORDER = {
  idle: "-border--theme-colors-ai-upload-border-idle",
  uploading: "-border--theme-colors-ai-upload-border-uploading",
  processing: "-border--theme-colors-ai-upload-border-processing",
} as const;

type Props = {
  maxSize?: number;
  onChange?: (files: File[]) => void;
  isLoading?: boolean;
  isProcessing?: boolean;
  uploadedFileName?: string | null;
  uploadedFileUrl?: string | null;
};

export default function AIUploadComponent({
  maxSize = 10 * 1024 * 1024,
  onChange,
  isLoading,
  isProcessing,
  uploadedFileName,
  uploadedFileUrl,
}: Props) {
  const { t } = useTranslation();
  const busy = isLoading || isProcessing;
  const { openPreview } = useDocumentPreview();

  const { getRootProps, getInputProps, open, isDragActive } = useDropzone({
    onDrop: (files) => onChange?.(files),
    maxSize,
    noClick: true,
    disabled: busy,
  });

  const subtitle =
    uploadedFileName ||
    (isDragActive
      ? t("AIUploadComponent.drop_files")
      : t("AIUploadComponent.drag_drop_browse"));

  const isUploaded = Boolean(uploadedFileName);

  const border = busy
    ? isLoading
      ? BORDER.uploading
      : BORDER.processing
    : BORDER.idle;

  return (
    <div
      {...getRootProps()}
      className={classNames(
        "relative w-full",
        busy && "pointer-events-none opacity-70",
      )}
    >
      <div
        className={classNames(
          "flex h-[83px] items-center justify-between gap-2 rounded-[12px] border border-solid p-5 -bg--theme-colors-ai-upload-card-bg",
          border,
        )}
      >
        <div className="flex min-w-0 flex-1 items-center gap-2">
          <div
            className={classNames(
              "flex size-[43px] shrink-0 items-center justify-center rounded-[10px] p-2",
              busy
                ? "animate-gradient-flow"
                : "bg-gradient-to-b from-[var(--theme-colors-ai-upload-icon-gradient-start)] to-[var(--theme-colors-ai-upload-icon-gradient-end)]",
            )}
            style={
              busy
                ? {
                    backgroundImage:
                      "linear-gradient(90deg, var(--theme-colors-ai-upload-icon-gradient-start), var(--theme-colors-ai-upload-icon-gradient-end), var(--theme-colors-ai-upload-icon-gradient-start), var(--theme-colors-ai-upload-icon-gradient-end))",
                    backgroundSize: "200% 100%",
                    backgroundPosition: "0% 50%",
                  }
                : undefined
            }
          >
            {isLoading ? (
              <Spinner size="sm" className="text-white" />
            ) : (
              <FontAwesomeIcon
                name="fa-sparkles"
                className="text-lg text-white"
              />
            )}
          </div>

          <div
            className={classNames(
              "flex min-w-0 flex-col justify-center",
              !busy && "gap-1",
            )}
          >
            {busy ? (
              <span className="text-sm font-normal leading-[1.5] -text--theme-colors-ai-upload-text-body">
                <LoadingTimelyMessager />
              </span>
            ) : (
              <>
                <div className="flex flex-wrap items-center gap-1">
                  <span className="text-base font-bold leading-6 -text--theme-colors-ai-upload-text-title">
                    {t("AIUploadComponent.title")}
                  </span>
                  <span className="flex h-[18px] min-w-[18px] items-center justify-center rounded-[4px] px-1.5 text-[8px] font-bold leading-none -bg--theme-colors-ai-upload-badge-bg -text--theme-colors-ai-upload-badge-text">
                    {t("AIUploadComponent.ai_badge")}
                  </span>
                </div>

                <button
                  type="button"
                  tabIndex={uploadedFileUrl ? 0 : -1}
                  aria-disabled={!uploadedFileUrl}
                  className={classNames(
                    "truncate text-left text-sm font-normal leading-[1.5] -text--theme-colors-ai-upload-text-body",
                    isUploaded && "underline",
                    uploadedFileUrl
                      ? "cursor-pointer hover:-text--theme-colors-ai-upload-text-title"
                      : "cursor-default",
                  )}
                  onClick={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    if (uploadedFileUrl) openPreview(uploadedFileUrl);
                  }}
                >
                  {subtitle}
                </button>
              </>
            )}
          </div>
        </div>

        <div className="shrink-0">
          <input type="file" className="hidden" {...getInputProps()} />
          <button
            type="button"
            disabled={busy}
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              if (!busy) open();
            }}
            className={classNames(
              "flex min-w-[100px] w-auto items-center justify-center gap-2 rounded-[8px] border border-solid px-3 py-2 text-xs font-bold leading-4 whitespace-nowrap transition-colors",
              busy
                ? "cursor-not-allowed -border--theme-colors-ai-upload-button-disabled-border -bg--theme-colors-ai-upload-button-disabled-bg -text--theme-colors-ai-upload-button-disabled-text"
                : "-border--theme-colors-ai-upload-button-border -bg--theme-colors-ai-upload-button-bg -text--theme-colors-ai-upload-button-text hover:-bg--theme-colors-ai-upload-button-hover-bg",
            )}
          >
            <FontAwesomeIcon
              name="fa-solid fa-arrow-up-from-bracket"
              className="text-base text-current"
            />
            {uploadedFileName
              ? t("AIUploadComponent.replace_file")
              : t("AIUploadComponent.browse")}
          </button>
        </div>
      </div>

      <div className="absolute -right-2 -top-1.5 flex h-4 w-[35px] items-center justify-center rounded-[4px] p-1 -bg--theme-colors-ai-upload-beta-bg -text--theme-colors-ai-upload-beta-text">
        <span className="text-[8px] font-bold leading-none">
          {t("AIUploadComponent.beta")}
        </span>
      </div>
    </div>
  );
}
